import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, X, Clock, User } from 'lucide-react';
import { Button } from './ui/button';

export const ImpersonationBanner = () => {
  const { isImpersonating, impersonationData, endImpersonation, getImpersonationTimeRemaining } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (isImpersonating) {
      const updateTimer = () => {
        const remaining = getImpersonationTimeRemaining();
        setTimeRemaining(remaining);
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      
      return () => clearInterval(interval);
    }
  }, [isImpersonating, getImpersonationTimeRemaining]);

  if (!isImpersonating || !impersonationData) return null;

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isExpiringSoon = timeRemaining !== null && timeRemaining < 120; // Less than 2 minutes

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[100] px-4 py-2 flex items-center justify-between ${
        isExpiringSoon ? 'bg-red-600' : 'bg-amber-500'
      } text-white shadow-lg`}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5" />
        <div className="flex items-center gap-2">
          <User className="h-4 w-4" />
          <span className="font-medium">
            Viewing as: <strong>{impersonationData.target_user?.name || impersonationData.target_user?.company_name}</strong>
          </span>
          <span className="text-white/80">
            ({impersonationData.type === 'customer' ? 'Customer Portal' : impersonationData.target_user?.role || 'User'})
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Clock className={`h-4 w-4 ${isExpiringSoon ? 'animate-pulse' : ''}`} />
          <span className="font-mono font-medium">
            {formatTime(timeRemaining)}
          </span>
          <span className="text-white/80 text-sm">remaining</span>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={endImpersonation}
          className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          data-testid="end-impersonation-btn"
        >
          <X className="h-4 w-4 mr-1" />
          Exit Impersonation
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
